import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import {
  MoreHorizontal,
  Edit3,
  Trash2,
  Reply,
  Check,
  X,
  AlertCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Comment {
  id: string;
  sequence_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  parent_comment_id: string | null;
  is_edited: boolean;
  user_profiles: {
    user_id: string;
    full_name: string | null;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
}

interface CommentItemProps {
  comment: Comment;
  onUpdate: () => void;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onUpdate
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = user?.id === comment.user_id;
  const maxLength = 2000;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString();
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(comment.content);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(comment.content);
    setError(null);
  };

  const handleSaveEdit = async () => {
    const trimmedContent = editContent.trim();

    if (trimmedContent.length === 0) {
      setError('Comment cannot be empty');
      return;
    }

    if (trimmedContent.length > maxLength) {
      setError(`Comment must be ${maxLength} characters or less`);
      return;
    }

    if (trimmedContent === comment.content) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('sequence_comments')
        .update({
          content: trimmedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', comment.id);

      if (updateError) throw updateError;

      setIsEditing(false);
      onUpdate();
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);

    try {
      const { error: deleteError } = await supabase
        .from('sequence_comments')
        .delete()
        .eq('id', comment.id);

      if (deleteError) throw deleteError;

      onUpdate();
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    } finally {
      setIsDeleting(false);
    }
  };

  const userName = comment.user_profiles?.full_name ||
                   comment.user_profiles?.display_name ||
                   comment.user_profiles?.username ||
                   'Anonymous User';
  const userAvatar = comment.user_profiles?.avatar_url;
  const userInitial = userName[0]?.toUpperCase() || 'U';
  const profileLink = comment.user_profiles?.username
    ? `/profile/${comment.user_profiles.username}`
    : `/profile/${comment.user_id}`;

  return (
    <Card className="w-full">
      <CardContent className="pt-4">
        <div className="space-y-3">
          {/* User info and actions */}
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <Avatar className="w-8 h-8">
                <AvatarImage src={userAvatar || undefined} alt={userName} />
                <AvatarFallback>{userInitial}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center space-x-2">
                  <Link
                    to={profileLink}
                    className="font-medium text-sm hover:underline"
                  >
                    {userName}
                  </Link>
                  <span className="text-xs text-gray-500">
                    {formatDate(comment.created_at)}
                  </span>
                  {comment.is_edited && (
                    <span className="text-xs text-gray-400">(edited)</span>
                  )}
                </div>
              </div>
            </div>

            {isOwner && !isEditing && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={isDeleting}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Comment content */}
          {isEditing ? (
            <div className="space-y-3">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[80px] resize-none"
                disabled={isUpdating}
                maxLength={maxLength}
              />
              <div className="flex justify-between items-center">
                <div className="text-xs text-gray-400">
                  {editContent.length}/{maxLength}
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={isUpdating || editContent.trim() === comment.content}
                  >
                    {isUpdating ? (
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-200 whitespace-pre-wrap">
              {comment.content}
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-center space-x-2 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions (reply, etc.) - for future threading */}
          {!isEditing && (
            <div className="flex items-center space-x-4 pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
                disabled // Will be enabled when threading is implemented
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentItem;